import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute } from "@tanstack/react-router"
import { Laptop, LogOut, Monitor, Moon, Smartphone, Sun, UserCircle } from "lucide-react"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { Profile } from "@/api/generated/Profile"
import type { SessionRow } from "@/api/generated/SessionRow"
import {
  useChangePassword,
  useMySessions,
  useProfile,
  useRevokeOtherSessions,
  useUpdateProfile,
} from "@/api/queries/me"
import { useConfirm } from "@/components/app/confirm-dialog"
import { TextField } from "@/components/app/fields"
import { FormRoot, FormRootError, SubmitButton } from "@/components/app/form"
import { KeyValueList } from "@/components/app/key-value"
import { PageContainer, PageHeader, PageSection, Panel } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { CardSkeleton, ErrorState, FormSkeleton } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { UserAvatar } from "@/components/app/user-avatar"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { useLanguage } from "@/hooks/use-language"
import i18n, { isLanguage, LANGUAGES } from "@/i18n"
import { applyApiErrorToForm } from "@/lib/form"
import { describeUserAgent, formatDate } from "@/lib/format"
import { z } from "@/lib/zod"
import { type Theme, useTheme } from "@/providers/theme"

export const Route = createFileRoute("/_app/settings/profile")({
  staticData: { titleKey: "nav.items.profile" },
  component: ProfilePage,
})

function ProfilePage() {
  const { t } = useTranslation()
  const profile = useProfile()

  return (
    <PageContainer>
      <PageHeader title={t("profile.title")} description={t("profile.description")} icon={UserCircle} />
      <div className="space-y-10">
        <PageSection
          layout="split"
          title={t("profile.details.title")}
          description={t("profile.details.description")}
        >
          {profile.isPending ? (
            <Panel>
              <FormSkeleton fields={2} />
            </Panel>
          ) : profile.isError ? (
            <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
          ) : (
            <ProfileForm profile={profile.data} />
          )}
        </PageSection>

        <PageSection
          layout="split"
          title={t("profile.preferences.title")}
          description={t("profile.preferences.description")}
        >
          <Preferences />
        </PageSection>

        <PageSection
          layout="split"
          title={t("profile.password.title")}
          description={t("profile.password.description")}
        >
          <PasswordForm />
        </PageSection>

        <PageSection
          id="sessions"
          layout="split"
          title={t("profile.sessions.title")}
          description={t("profile.sessions.description")}
        >
          <SessionsList />
        </PageSection>
      </div>
    </PageContainer>
  )
}

const profileSchema = z.object({
  display_name: z.string().trim().max(150),
  email: z.union([z.literal(""), z.email()]),
})
type ProfileValues = z.infer<typeof profileSchema>

function ProfileForm({ profile }: { profile: Profile }) {
  const { t } = useTranslation()
  const update = useUpdateProfile()
  const form = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { display_name: profile.display_name, email: profile.email ?? "" },
  })
  useEffect(() => {
    form.reset({ display_name: profile.display_name, email: profile.email ?? "" })
  }, [profile, form])

  return (
    <FormRoot
      form={form}
      onSubmit={async (v) => {
        await update.mutateAsync({ display_name: v.display_name, email: v.email })
      }}
    >
      <Panel
        footer={
          <SubmitButton form={form} disabledWhenPristine size="sm">
            {t("common.save")}
          </SubmitButton>
        }
      >
        <div className="mb-5 flex items-center gap-3">
          <UserAvatar name={profile.display_name || profile.username} seed={profile.username} size="lg" />
          <div className="min-w-0">
            <div className="truncate font-medium">{profile.display_name || profile.username}</div>
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <span className="font-mono">@{profile.username}</span>
              {profile.is_platform_admin && (
                <StatusBadge tone="accent">{t("profile.platformAdmin")}</StatusBadge>
              )}
            </div>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            control={form.control}
            name="display_name"
            label={t("profile.details.displayName")}
            autoComplete="name"
            placeholder={profile.username}
          />
          <TextField
            control={form.control}
            name="email"
            type="email"
            label={t("profile.details.email")}
            autoComplete="email"
            placeholder={t("profile.details.emailPlaceholder")}
          />
        </div>
        <KeyValueList
          className="mt-5 border-t pt-4"
          items={[
            {
              label: t("profile.details.username"),
              value: profile.username,
              mono: true,
              copy: profile.username,
            },
            { label: t("profile.details.memberSince"), value: formatDate(profile.created_at) },
            { label: t("profile.details.lastLogin"), value: <RelativeTime value={profile.last_login_at} /> },
          ]}
        />
        <FormRootError form={form} className="mt-4" />
      </Panel>
    </FormRoot>
  )
}

function Preferences() {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  const { language, setLanguage } = useLanguage()
  const themes = [
    { value: "light", label: t("theme.light"), icon: Sun },
    { value: "dark", label: t("theme.dark"), icon: Moon },
    { value: "system", label: t("theme.system"), icon: Monitor },
  ] as const
  return (
    <Panel>
      <div className="grid gap-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[0.8125rem] font-medium">{t("theme.label")}</div>
            <div className="text-muted-foreground text-xs">{t("profile.preferences.themeHint")}</div>
          </div>
          <ToggleGroup
            value={[theme]}
            onValueChange={(v: unknown[]) => v[0] && setTheme(v[0] as Theme)}
            variant="outline"
            size="sm"
            aria-label={t("theme.label")}
          >
            {themes.map(({ value, label, icon: Icon }) => (
              <ToggleGroupItem key={value} value={value} aria-label={label} className="gap-1.5 px-2.5">
                <Icon />
                {label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div className="flex flex-col gap-2 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[0.8125rem] font-medium">{t("language.label")}</div>
            <div className="text-muted-foreground text-xs">{t("profile.preferences.languageHint")}</div>
          </div>
          <ToggleGroup
            value={[language]}
            onValueChange={(v: unknown[]) => isLanguage(v[0]) && void setLanguage(v[0])}
            variant="outline"
            size="sm"
            aria-label={t("language.label")}
          >
            {LANGUAGES.map((l) => (
              <ToggleGroupItem key={l.code} value={l.code} className="px-2.5">
                {l.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
    </Panel>
  )
}

const passwordSchema = z
  .object({
    current_password: z.string().min(1),
    new_password: z
      .string()
      .min(10)
      .refine((v) => !/^\d+$/.test(v), { error: () => i18n.t("profile.password.notNumeric") }),
    confirm: z.string().min(1),
  })
  .refine((v) => v.new_password === v.confirm, {
    path: ["confirm"],
    error: () => i18n.t("profile.password.mismatch"),
  })
type PasswordValues = z.infer<typeof passwordSchema>

function PasswordForm() {
  const { t } = useTranslation()
  const change = useChangePassword()
  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  })
  return (
    <FormRoot
      form={form}
      onSubmit={async (v) => {
        try {
          await change.mutateAsync({ current_password: v.current_password, new_password: v.new_password })
          form.reset()
        } catch (e) {
          // the server reports new-password problems on "password"
          applyApiErrorToForm(e, form, { fieldMap: { password: "new_password" } })
        }
      }}
    >
      <Panel
        footer={
          <SubmitButton form={form} disabledWhenPristine size="sm">
            {t("profile.password.submit")}
          </SubmitButton>
        }
      >
        <div className="grid gap-4">
          <TextField
            control={form.control}
            name="current_password"
            type="password"
            label={t("profile.password.current")}
            autoComplete="current-password"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="new_password"
              type="password"
              label={t("profile.password.new")}
              autoComplete="new-password"
              description={t("profile.password.rules")}
            />
            <TextField
              control={form.control}
              name="confirm"
              type="password"
              label={t("profile.password.confirm")}
              autoComplete="new-password"
            />
          </div>
          <p className="text-muted-foreground text-xs">{t("profile.password.signsOutOthers")}</p>
          <FormRootError form={form} />
        </div>
      </Panel>
    </FormRoot>
  )
}

function SessionIcon({ row }: { row: SessionRow }) {
  return /Mobile|iPhone|Android/i.test(row.user_agent) ? (
    <Smartphone className="size-4" />
  ) : (
    <Laptop className="size-4" />
  )
}

function SessionsList() {
  const { t } = useTranslation()
  const sessions = useMySessions()
  const revoke = useRevokeOtherSessions()
  const confirm = useConfirm()
  const others = sessions.data?.filter((s) => !s.current).length ?? 0

  if (sessions.isPending)
    return (
      <div className="space-y-2">
        <CardSkeleton />
        <Skeleton className="h-16 rounded-xl" />
      </div>
    )
  if (sessions.isError) return <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />

  const rows = [...sessions.data].sort((a, b) => Number(b.current) - Number(a.current))
  return (
    <Panel
      footer={
        <Button
          variant="destructive"
          size="sm"
          disabled={others === 0 || revoke.isPending}
          onClick={() =>
            void confirm({
              title: t("profile.sessions.revokeTitle"),
              description: t("profile.sessions.revokeDescription", { count: others }),
              confirmLabel: t("profile.sessions.revoke"),
              destructive: true,
              onConfirm: () => revoke.mutateAsync(),
            })
          }
        >
          <LogOut />
          {t("profile.sessions.revoke")}
        </Button>
      }
    >
      <ul className="-my-2 divide-y">
        {rows.map((row) => {
          const ua = describeUserAgent(row.user_agent)
          return (
            <li key={row.id} className="flex items-center gap-3 py-3">
              <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
                <SessionIcon row={row} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="truncate">
                    {ua.os ? t("profile.sessions.device", { browser: ua.browser, os: ua.os }) : ua.browser}
                  </span>
                  {row.current && (
                    <StatusBadge tone="success" dot>
                      {t("profile.sessions.current")}
                    </StatusBadge>
                  )}
                </div>
                <div className="text-muted-foreground flex flex-wrap gap-x-2 text-xs">
                  <span>{row.ip ?? t("profile.sessions.unknownIp")}</span>
                  <span aria-hidden>·</span>
                  <span>
                    {t("profile.sessions.lastActive")} <RelativeTime value={row.last_seen_at} />
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    {t("profile.sessions.signedIn")} {formatDate(row.created_at)}
                  </span>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}
